import { Input } from '@components/ui/input';
import { Textarea } from '@components/ui/textarea';
import { Label } from '@components/ui/label';
import { Upload } from 'lucide-react';
import { PAYMENT_METHODS } from '@generated/components/pricing';

export interface Details {
  businessName: string; contactPerson: string; email: string; phone: string; address: string;
  artworkLink: string; paymentMethod: string; notes: string;
}

interface Props { d: Details; onChange: (d: Details) => void; file: File | null; onFile: (f: File | null) => void; deadline: string; video?: boolean; showScript?: boolean; scriptFile?: File | null; onScriptFile?: (f: File | null) => void }

export function StepDetails({ d, onChange, file, onFile, deadline, video = false, showScript = false, scriptFile = null, onScriptFile }: Props) {
  const set = (k: keyof Details) => (e: { target: { value: string } }) => onChange({ ...d, [k]: e.target.value });
  const field = (k: keyof Details, label: string, type = 'text') => (
    <div className="space-y-1.5">
      <Label htmlFor={k}>{label}</Label>
      <Input id={k} type={type} value={d[k]} onChange={set(k)} className="h-11 rounded-none bg-card" />
    </div>
  );
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <section className="space-y-4">
        <h2 className="font-[family-name:var(--font-heading)] text-2xl font-semibold">Advertiser</h2>
        {field('businessName', 'Advertiser / Business name *')}
        {field('contactPerson', 'Contact person *')}
        <div className="grid gap-4 sm:grid-cols-2">{field('email', 'Email *', 'email')}{field('phone', 'Phone *', 'tel')}</div>
        {field('address', 'Billing / business address')}
        <div className="space-y-2">
          <Label>Payment method</Label>
          <div className="flex flex-wrap gap-2">
            {PAYMENT_METHODS.map((m) => (
              <button key={m} type="button" onClick={() => onChange({ ...d, paymentMethod: m })}
                className={`min-h-10 border px-3 text-sm ${d.paymentMethod === m ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:border-foreground/50'}`}>{m}</button>
            ))}
          </div>
        </div>
      </section>
      <section className="space-y-4">
        <h2 className="font-[family-name:var(--font-heading)] text-2xl font-semibold">{video ? "Ad script" : "Artwork"}</h2>
        <div className="border-l-4 border-primary bg-muted/60 p-3 text-sm">
          {video ? "Send your script for the two 20-second spots (PDF, Word or text). Ad script deadline:" : "Accepted: PDF (300 dpi), JPG, PNG. Artwork deadline:"} <strong>{deadline}</strong> (25th of the prior month).
        </div>
        <label className="flex min-h-28 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-foreground/40 bg-card p-4 text-center text-sm hover:border-primary">
          <Upload className="h-5 w-5 text-muted-foreground" />
          <span className="max-w-full truncate">{file ? file.name : video ? "Upload ad script" : "Upload artwork file"}</span>
          <input type="file" accept={video ? ".pdf,.doc,.docx,.txt" : ".pdf,.jpg,.jpeg,.png"} className="sr-only" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
        </label>
        {showScript && (
          <div className="space-y-2 border-t border-border pt-4">
            <div className="font-[family-name:var(--font-heading)] text-lg font-semibold">Brennan's Report ad script</div>
            <p className="text-sm text-muted-foreground">Script for your two 20-second spots (PDF, Word or text). Due {deadline}.</p>
            <label className="flex min-h-20 cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-foreground/40 bg-card p-4 text-center text-sm hover:border-primary">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <span className="max-w-full truncate">{scriptFile ? scriptFile.name : "Upload ad script"}</span>
              <input type="file" accept=".pdf,.doc,.docx,.txt" className="sr-only" onChange={(e) => onScriptFile?.(e.target.files?.[0] ?? null)} />
            </label>
          </div>
        )}
        {field('artworkLink', 'Or paste a link (Dropbox, Google Drive…)', 'url')}
        <div className="space-y-1.5">
          <Label htmlFor="notes">Special instructions / placement requests</Label>
          <Textarea id="notes" rows={5} value={d.notes} onChange={set('notes')} className="rounded-none bg-card" />
        </div>
      </section>
    </div>
  );
}
